import axios from 'axios';
import { ApiResponse, FormData, TenderAnalysisState } from '../types';

// Configure axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://api-beta-open-tender.nat-quest.com' 
      : 'http://localhost:2024'),
  timeout: 300000, // 5 minutes timeout for long-running processes
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
const TOKEN_STORAGE_KEY = 'opentender_tokens';

const getAccessToken = (): string | null => {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) return null;
    
    const { accessToken } = JSON.parse(stored);
    return accessToken;
  } catch {
    return null;
  }
};

const getRefreshToken = (): string | null => {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) return null;
    
    const { refreshToken } = JSON.parse(stored);
    return refreshToken;
  } catch {
    return null;
  }
};

const updateAccessToken = (newAccessToken: string): void => {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored) {
      const tokenData = JSON.parse(stored);
      tokenData.accessToken = newAccessToken;
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
    }
  } catch {
    // Ignore errors
  }
};

// Request interceptor to add authentication
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If we get a 401 and haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          // Attempt to refresh the token
          const refreshResponse = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL || 
              (process.env.NODE_ENV === 'production' 
                ? 'https://api-beta-open-tender.nat-quest.com' 
                : 'http://localhost:2024')}/auth/refresh`,
            {},
            {
              headers: {
                'Authorization': `Bearer ${refreshToken}`,
                'Content-Type': 'application/json',
              },
            }
          );
          
          const { access_token } = refreshResponse.data;
          updateAccessToken(access_token);
          
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        window.location.href = '/login';
      }
    }
    
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Authentication API class
export class AuthAPI {
  /**
   * Login with email and password
   */
  static async login(email: string, password: string): Promise<ApiResponse> {
    try {
      const response = await api.post('/auth/login', { email, password });
      return {
        success: true,
        data: response.data,
        message: 'Login successful',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Login failed',
      };
    }
  }

  /**
   * Pilot self-onboarding registration. Backend gates this against the
   * pilot_whitelist collection — 403 if email isn't on the list, 409 if an
   * account already exists. On success the backend also emails a magic link.
   */
  static async register(payload: {
    email: string;
    first_name: string;
    last_name?: string;
    organization_name?: string;
    password: string;
  }): Promise<ApiResponse> {
    try {
      const response = await api.post('/auth/register', payload);
      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'Account created',
      };
    } catch (error: any) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      let friendly = detail || error.message || 'Registration failed';
      if (status === 403 && !detail) {
        friendly = "This email isn't on the pilot list yet.";
      } else if (status === 409 && !detail) {
        friendly = 'An account already exists for this email.';
      }
      return {
        success: false,
        error: friendly,
        data: { status },
      };
    }
  }

  /**
   * Request magic link
   */
  static async requestMagicLink(email: string): Promise<ApiResponse> {
    try {
      const response = await api.post('/auth/magic-link', { email });
      return {
        success: true,
        data: response.data,
        message: 'Magic link sent',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to send magic link',
      };
    }
  }

  /**
   * Verify magic link
   */
  static async verifyMagicLink(token: string): Promise<ApiResponse> {
    try {
      const response = await api.get(`/auth/verify-magic-link?token=${encodeURIComponent(token)}`);
      return {
        success: true,
        data: response.data,
        message: 'Magic link verified',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Invalid magic link',
      };
    }
  }

  /**
   * Request a password reset email. Backend always returns 200 to prevent
   * account enumeration — so the response is the same regardless of whether
   * the email matches a real user.
   */
  static async requestPasswordReset(email: string): Promise<ApiResponse> {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'If an account exists, a reset link is on its way.',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Could not send reset link',
      };
    }
  }

  /**
   * Pre-flight check the reset token before showing the new-password form.
   * Returns { valid: boolean, email?: string }.
   */
  static async verifyResetToken(token: string): Promise<ApiResponse> {
    try {
      const response = await api.get(
        `/auth/verify-reset-token?token=${encodeURIComponent(token)}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Invalid reset link',
      };
    }
  }

  /**
   * Submit a new password with the reset token. Backend hashes + saves it.
   * On success, the user goes to /login to sign in (no auto-login).
   */
  static async resetPassword(token: string, newPassword: string): Promise<ApiResponse> {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        new_password: newPassword,
      });
      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'Password updated',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Could not reset password',
      };
    }
  }

  /**
   * Get current user
   */
  static async getCurrentUser(): Promise<ApiResponse> {
    try {
      const response = await api.get('/auth/me');
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to get user info',
      };
    }
  }

  /**
   * Logout
   */
  static async logout(): Promise<ApiResponse> {
    try {
      await api.post('/auth/logout');
      return {
        success: true,
        message: 'Logged out successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Logout failed',
      };
    }
  }

  /**
   * Refresh access token
   */
  static async refreshToken(): Promise<ApiResponse> {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post('/auth/refresh', {}, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`,
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Token refresh failed',
      };
    }
  }

  /**
   * Get user profile including settings
   */
  static async getUserProfile(): Promise<ApiResponse> {
    try {
      const response = await api.get('/auth/profile');
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to get user profile',
      };
    }
  }

  /**
   * Update user profile
   */
  static async updateUserProfile(profileData: any): Promise<ApiResponse> {
    try {
      const response = await api.put('/auth/profile', profileData);
      return {
        success: true,
        data: response.data,
        message: 'Profile updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to update profile',
      };
    }
  }
}

// Admin API class
export class AdminAPI {
  /**
   * Get all users (admin only)
   */
  static async getUsers(skip: number = 0, limit: number = 100): Promise<ApiResponse> {
    try {
      const response = await api.get(`/admin/users?skip=${skip}&limit=${limit}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to get users',
      };
    }
  }

  /**
   * Create user (admin only)
   */
  static async createUser(userData: any): Promise<ApiResponse> {
    try {
      const response = await api.post('/admin/users', userData);
      return {
        success: true,
        data: response.data,
        message: 'User created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to create user',
      };
    }
  }

  /**
   * Update user (admin only)
   */
  static async updateUser(userId: string, userData: any): Promise<ApiResponse> {
    try {
      const response = await api.put(`/admin/users/${userId}`, userData);
      return {
        success: true,
        data: response.data,
        message: 'User updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to update user',
      };
    }
  }

  /**
   * Delete user (admin only)
   */
  static async deleteUser(userId: string): Promise<ApiResponse> {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      return {
        success: true,
        data: response.data,
        message: 'User deleted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to delete user',
      };
    }
  }

  /**
   * Get user by ID (admin only)
   */
  static async getUser(userId: string): Promise<ApiResponse> {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to get user',
      };
    }
  }
}

export class TenderAnalysisAPI {
  /**
   * Submit tender analysis request
   */
  static async submitTenderAnalysis(formData: FormData): Promise<ApiResponse> {
    try {
      const response = await api.post('/threads', {
        input: {
          url: formData.url,
          successful_tender_urls: formData.successful_tender_urls,
          company_id: formData.company_id,
        },
        config: {
          configurable: {},
        },
        multitask_strategy: 'reject',
      });

      return {
        success: true,
        data: response.data,
        message: 'Analysis started successfully',
      };
    } catch (error: any) {
      console.error('Submit tender analysis error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to submit analysis',
      };
    }
  }

  /**
   * Get analysis status and results
   */
  static async getAnalysisStatus(threadId: string): Promise<ApiResponse> {
    try {
      const response = await api.get(`/threads/${threadId}/state`);
      
      return {
        success: true,
        data: response.data.values as TenderAnalysisState,
      };
    } catch (error: any) {
      console.error('Get analysis status error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to get analysis status',
      };
    }
  }

  /**
   * Stream analysis progress (for real-time updates)
   */
  static async streamAnalysis(threadId: string, onUpdate: (data: TenderAnalysisState) => void): Promise<void> {
    try {
      const response = await api.get(`/threads/${threadId}/runs`, {
        responseType: 'stream',
      });

      // Handle streaming response
      const reader = response.data.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.values) {
                onUpdate(data.values as TenderAnalysisState);
              }
            } catch (e) {
              console.warn('Failed to parse streaming data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Stream analysis error:', error);
      throw error;
    }
  }

  /**
   * Get available graphs/agents
   */
  static async getAvailableGraphs(): Promise<ApiResponse> {
    try {
      const response = await api.get('/assistants');
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Get available graphs error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to get available graphs',
      };
    }
  }

  /**
   * Health check
   */
  static async healthCheck(): Promise<boolean> {
    try {
      const response = await api.get('/health');
      return response.status === 200;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }

  /**
   * Get tender bank documents for a company
   */
  static async getTenderBankDocuments(companyId: string, filters?: any): Promise<ApiResponse> {
    try {
      const params = new URLSearchParams();
      params.append('company_id', companyId);
      
      if (filters) {
        Object.keys(filters).forEach(key => {
          if (filters[key]) {
            params.append(key, filters[key]);
          }
        });
      }

      const response = await api.get(`/tender-bank?${params.toString()}`);
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Get tender bank documents error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to get tender bank documents',
      };
    }
  }

  /**
   * Get single tender document details
   */
  static async getTenderDocument(id: string): Promise<ApiResponse> {
    try {
      const response = await api.get(`/tender-bank/${id}`);
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Get tender document error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to get tender document',
      };
    }
  }

  /**
   * Upload tender documents to the bank
   */
  static async uploadTenderDocuments(uploads: any[]): Promise<ApiResponse> {
    try {
      const response = await api.post('/tender-bank/upload', {
        documents: uploads,
      });
      
      return {
        success: true,
        data: response.data,
        message: 'Documents uploaded successfully',
      };
    } catch (error: any) {
      console.error('Upload tender documents error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to upload documents',
      };
    }
  }

  /**
   * Delete tender document from bank
   */
  static async deleteTenderDocument(id: string): Promise<ApiResponse> {
    try {
      const response = await api.delete(`/tender-bank/${id}`);
      
      return {
        success: true,
        data: response.data,
        message: 'Document deleted successfully',
      };
    } catch (error: any) {
      console.error('Delete tender document error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete document',
      };
    }
  }

  /**
   * Update tender document metadata
   */
  static async updateTenderDocument(id: string, updates: any): Promise<ApiResponse> {
    try {
      const response = await api.patch(`/tender-bank/${id}`, updates);
      
      return {
        success: true,
        data: response.data,
        message: 'Document updated successfully',
      };
    } catch (error: any) {
      console.error('Update tender document error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update document',
      };
    }
  }

  /**
   * Get tender bank analytics
   */
  static async getTenderBankAnalytics(companyId: string): Promise<ApiResponse> {
    try {
      const response = await api.get(`/tender-bank/analytics?company_id=${companyId}`);
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Get tender bank analytics error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to get analytics',
      };
    }
  }
}

// Dashboard API class for user statistics
export class DashboardAPI {
  /**
   * Get user projects count
   */
  static async getProjectsCount(userId: string): Promise<ApiResponse> {
    try {
      const response = await api.get(`/projects/count?user_id=${userId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Get projects count error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to get projects count',
      };
    }
  }

  /**
   * Get user analyses count
   */
  static async getAnalysesCount(userId: string): Promise<ApiResponse> {
    try {
      const response = await api.get(`/analyses/count?user_id=${userId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Get analyses count error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to get analyses count',
      };
    }
  }

  /**
   * Get user tender bank documents count
   */
  static async getTenderBankCount(userId: string): Promise<ApiResponse> {
    try {
      const response = await api.get(`/tender-bank/count?user_id=${userId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Get tender bank count error:', error);  
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to get tender bank count',
      };
    }
  }

  /**
   * Get combined user statistics for dashboard
   */
  static async getUserStats(userId: string): Promise<ApiResponse> {
    try {
      console.log('Making API call to get user stats for user ID:', userId);
      const response = await api.get(`/auth/dashboard/stats?user_id=${userId}`);
      console.log('User stats API response:', response.data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Get user stats error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to get user statistics',
      };
    }
  }

  /**
   * Get user recent activities for dashboard
   */
  static async getUserActivities(userId: string, limit: number = 10): Promise<ApiResponse> {
    try {
      const response = await api.get(`/auth/dashboard/activities?user_id=${userId}&limit=${limit}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Get user activities error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to get user activities',
      };
    }
  }
}

// Analysis Sessions API class
export class AnalysisAPI {
  /**
   * Create a new analysis session
   */
  static async createAnalysisSession(sessionData: {
    thread_id: string;
    project_id?: string;
    input_data: {
      url: string;
      company_id?: string;
      successful_tender_urls: string[];
      analysis_type: string;
    };
    tags?: string[];
  }): Promise<ApiResponse> {
    try {
      const response = await api.post('/api/analysis/sessions', sessionData);
      return {
        success: true,
        data: response.data,
        message: 'Analysis session created successfully',
      };
    } catch (error: any) {
      console.error('Create analysis session error:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Request data:', sessionData);
      
      // If it's an authentication error, continue without persisting the session
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('User not authenticated, proceeding without session persistence');
        return {
          success: true,
          data: { id: 'guest-session', thread_id: sessionData.thread_id },
          message: 'Analysis session running in guest mode (not persisted)',
        };
      }
      
      // If it's a 400 error, also try guest mode as fallback
      if (error.response?.status === 400) {
        console.warn('Bad request (likely auth issue), proceeding without session persistence');
        return {
          success: true,
          data: { id: 'guest-session', thread_id: sessionData.thread_id },
          message: 'Analysis session running in guest mode (validation failed)',
        };
      }
      
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to create analysis session',
      };
    }
  }

  /**
   * Get analysis session by thread ID
   */
  static async getAnalysisSession(threadId: string): Promise<ApiResponse> {
    try {
      const response = await api.get(`/api/analysis/sessions/${threadId}`);
      if (response.data == null) {
        return { success: false, error: 'not_found' };
      }
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to get analysis session',
      };
    }
  }

  /**
   * Update analysis session results
   */
  static async updateAnalysisResults(threadId: string, results: any): Promise<ApiResponse> {
    try {
      const response = await api.put(`/api/analysis/sessions/${threadId}/results`, {
        results,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to update analysis results',
      };
    }
  }

  /**
   * Update refined questions in analysis session
   */
  static async updateRefinedQuestions(threadId: string, refinedQuestions: any[]): Promise<ApiResponse> {
    try {
      const response = await api.put(`/api/analysis/sessions/${threadId}/refined-questions`, {
        refined_questions: refinedQuestions,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to update refined questions',
      };
    }
  }

  /**
   * Update analysis session status
   */
  static async updateAnalysisStatus(
    threadId: string,
    status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled',
    currentStep?: string,
    errorInfo?: any
  ): Promise<ApiResponse> {
    try {
      const response = await api.put(`/api/analysis/sessions/${threadId}/status`, {
        status,
        current_step: currentStep,
        error_info: errorInfo,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to update analysis status',
      };
    }
  }

  /**
   * Update analysis session metrics
   */
  static async updateAnalysisMetrics(threadId: string, metrics: any): Promise<ApiResponse> {
    try {
      const response = await api.put(`/api/analysis/sessions/${threadId}/metrics`, {
        metrics,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to update analysis metrics',
      };
    }
  }

  /**
   * List user's analysis sessions
   */
  static async listAnalysisSessions(params?: {
    page?: number;
    page_size?: number;
    status_filter?: string;
    project_id?: string;
  }): Promise<ApiResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
      if (params?.status_filter) queryParams.append('status_filter', params.status_filter);
      if (params?.project_id) queryParams.append('project_id', params.project_id);

      const response = await api.get(`/api/analysis/sessions?${queryParams.toString()}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to list analysis sessions',
      };
    }
  }

  /**
   * Delete analysis session
   */
  static async deleteAnalysisSession(threadId: string): Promise<ApiResponse> {
    try {
      const response = await api.delete(`/api/analysis/sessions/${threadId}`);
      return {
        success: true,
        data: response.data,
        message: 'Analysis session deleted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to delete analysis session',
      };
    }
  }

  /**
   * Get analysis metrics for user
   */
  static async getAnalysisMetrics(params?: {
    metric_type?: string;
    days_back?: number;
  }): Promise<ApiResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.metric_type) queryParams.append('metric_type', params.metric_type);
      if (params?.days_back) queryParams.append('days_back', params.days_back.toString());

      const response = await api.get(`/api/analysis/metrics?${queryParams.toString()}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to get analysis metrics',
      };
    }
  }


  /**
   * Get user settings
   */
  static async getUserSettings(): Promise<ApiResponse> {
    try {
      const response = await api.get('/auth/settings');
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to get user settings',
      };
    }
  }

  /**
   * Update user settings
   */
  static async updateUserSettings(settingsData: any): Promise<ApiResponse> {
    try {
      const response = await api.put('/auth/settings', settingsData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to update settings',
      };
    }
  }

  /**
   * Reset user settings to default
   */
  static async resetUserSettings(): Promise<ApiResponse> {
    try {
      const response = await api.post('/auth/settings/reset');
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to reset settings',
      };
    }
  }

  /**
   * Complete user onboarding
   */
  static async completeOnboarding(): Promise<ApiResponse> {
    try {
      const response = await api.post('/auth/complete-onboarding');
      return {
        success: true,
        data: response.data,
        message: 'Onboarding completed successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to complete onboarding',
      };
    }
  }
}

export default api;